import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAppContext } from '@/contexts/AppContext';
import { toast } from '@/components/ui/use-toast';
import type { BonusTier } from '@/contexts/AppContext';

const BonusTierManagement: React.FC = () => {
  const { adminSettings, updateAdminSettings } = useAppContext();
  const [tiers, setTiers] = useState<BonusTier[]>(adminSettings.bonusTiers);

  const handleTierChange = (index: number, field: keyof BonusTier, value: string) => {
    const newTiers = [...tiers];
    if (field === 'bonus') {
      newTiers[index][field] = parseFloat(value) || 0;
    } else {
      newTiers[index][field] = field === 'max' && value === 'Infinity' ? Infinity : parseFloat(value) || 0;
    }
    setTiers(newTiers);
  };

  const handleSave = () => {
    updateAdminSettings({ bonusTiers: tiers });
    toast({
      title: 'Success',
      description: 'Bonus tiers updated successfully'
    });
  };

  const handleReset = () => {
    setTiers(adminSettings.bonusTiers);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg text-purple-800">
          Bonus Tier Management
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-3">
          {tiers.map((tier, index) => (
            <div key={index} className="grid grid-cols-4 gap-3 items-center p-3 border rounded-lg">
              <div>
                <Label className="text-xs">Min ($)</Label>
                <Input
                  type="number"
                  value={tier.min}
                  onChange={(e) => handleTierChange(index, 'min', e.target.value)}
                  className="h-8"
                />
              </div>
              <div>
                <Label className="text-xs">Max ($)</Label>
                <Input
                  type="number"
                  value={tier.max === Infinity ? '' : tier.max}
                  placeholder={tier.max === Infinity ? '∞' : ''}
                  onChange={(e) => handleTierChange(index, 'max', e.target.value || 'Infinity')}
                  className="h-8"
                />
              </div>
              <div>
                <Label className="text-xs">Bonus ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={tier.bonus}
                  onChange={(e) => handleTierChange(index, 'bonus', e.target.value)}
                  className="h-8"
                />
              </div>
              <div className="text-sm text-gray-600">
                ${tier.min} - {tier.max === Infinity ? '∞' : `$${tier.max}`}
              </div>
            </div>
          ))}
        </div>
        
        <div className="flex space-x-2 pt-4">
          <Button onClick={handleSave} className="bg-green-600 hover:bg-green-700">
            Save Tiers
          </Button>
          <Button onClick={handleReset} variant="outline">
            Reset
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default BonusTierManagement;